import { NetworkStatus } from "@apollo/client";
import { InMemoryCache, type TypePolicies } from "@apollo/client/cache";
import type { CatalogueCoding } from "elephant-web/gql";
import { apolloCacheTypePolicies } from "./type-policies";

/**
 * We require a custom id identifier due to the use of uuids
 * @param {*} object is the object from the store
 */
export function customDataIdFromObject(object: {
  __typename?: string | null;
  id?: string | null;
  _id?: string | null;
  uuid?: string | null;
}): string | undefined {
  const { __typename, id, _id, uuid } = object;

  // This custom logic is required as CatalogueCoding does not have its own id,
  // we must get it by combining elephant.code and loinc.code,
  // and either of these objects could be null
  if (__typename === "CatalogueCoding") {
    const coding = object as CatalogueCoding;
    return `${__typename}:l-${coding.loinc?.code}:e-${coding.elephant?.code}`;
  }

  if (typeof __typename === "string") {
    if (typeof id === "string") return `${__typename}:${id}`;
    if (typeof _id === "string") return `${__typename}:${_id}`;
    if (typeof uuid === "string") return `${__typename}:${uuid}`;
  }
  return undefined;
}

export const newCache = () =>
  new InMemoryCache({
    dataIdFromObject: customDataIdFromObject,
    typePolicies: apolloCacheTypePolicies as unknown as TypePolicies,
    // https://www.apollographql.com/docs/react/data/fragments/#using-fragments-with-unions-and-interfaces
    possibleTypes: {
      StockTakeLot: ["StockTakeLotUpdate", "StockTakeLotAddition"],
    },
  }); /*
 * As per the docs, networkStatus < 7 is the equivalent of the
 * `loading` variable from useQuery/useMutation equalling true:
 * https://www.apollographql.com/docs/react/api/react/hoc/#datanetworkstatus
 *
 * Passing only `networkStatus` makes the interface simpler, and the
 * logic and tests easier to understand, as you do not have two
 * variables representing the same "real world" state.
 */

export function isCacheAndNetworkLoading({
  data,
  networkStatus,
}: {
  data?: unknown;
  networkStatus: NetworkStatus;
}): boolean {
  return (
    (networkStatus < 7 && !data) || networkStatus === NetworkStatus.refetch
  );
}
